'use client';

import { Elements, ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { useTranslations } from 'next-intl';
import React, { useEffect, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import ErrorLabel from '@cineamo/ui/src/label/ErrorLabel';

import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import type { BuyerDetails } from './actions';
import { reportBookingError, trackBookingEvent, trackPaymentConfirmed } from './booking-telemetry-client';
import MockWalletButton, { mockWallet } from './MockWalletButton';
import { buyerFromWallet } from './wallet-buyer';

export type Wallet = 'apple_pay' | 'google_pay';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY ?? '');

type ExpressCheckoutProps = {
    /** What Stripe charges, in cents (the cart's amount due). */
    amount: number;
    currency: string;
    /** Creates the PaymentIntent for the buyer; its client secret, 'paid' when nothing is due, or null after an error it has shown. */
    requestPaymentIntent: (buyer: BuyerDetails) => Promise<string | 'paid' | null>;
    onPaymentComplete: () => void;
    /** Which wallet this device offers, if any: the checkout lists it as a payment method. */
    onAvailabilityChange: (wallet: Wallet | null) => void;
    className?: string;
};

function ExpressCheckoutButtons({
    requestPaymentIntent,
    onPaymentComplete,
    onAvailabilityChange,
    amount,
    className
}: Omit<ExpressCheckoutProps, 'currency'>) {
    const stripe = useStripe();
    const elements = useElements();
    const { setIsProcessing } = useCartProvider();
    const t = useTranslations('tickets');
    const [isAvailable, setIsAvailable] = useState(false);
    const [message, setMessage] = useState<string>();

    /** The payment after the wallet sheet: create the PaymentIntent for the buyer, then confirm it. */
    const pay = async (
        buyer: BuyerDetails,
        confirm: (
            clientSecret: string
        ) => Promise<{ error?: { message?: string; type?: string; code?: string; decline_code?: string } }>,
        onFailed?: () => void
    ) => {
        setMessage(undefined);
        // The footer's spinner, and the countdown knows a payment is under way.
        setIsProcessing(true);
        trackBookingEvent('booking_payment_submitted', { method: 'wallet', amount_due: amount });
        try {
            const clientSecret = await requestPaymentIntent(buyer);
            if (clientSecret === null) {
                onFailed?.();
                return;
            }
            if (clientSecret === 'paid') {
                trackBookingEvent('booking_payment_confirmed', { outcome: 'paid', method: 'wallet' });
                onPaymentComplete();
                return;
            }
            const { error } = await confirm(clientSecret);
            trackPaymentConfirmed('wallet', error);
            if (error) {
                setMessage(error.message);
                return;
            }
            // Authorized (manual capture): the webhook captures and fulfills, the success view follows the order.
            onPaymentComplete();
        } catch (error) {
            // The wallet sheet is already closed on the buyer; tell them, and tell us (CIN-15436).
            reportBookingError({ step: 'confirm-payment', code: 'unexpected', error });
            setMessage(t('error.something-went-wrong'));
            onFailed?.();
        } finally {
            setIsProcessing(false);
        }
    };

    const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
        if (!stripe || !elements) {
            reportBookingError({ step: 'confirm-payment', code: 'stripe-not-loaded' });
            event.paymentFailed({ reason: 'fail' });
            return;
        }
        const { error: submitError } = await elements.submit();
        if (submitError) {
            event.paymentFailed({ reason: 'fail' });
            setMessage(submitError.message);
            return;
        }
        await pay(
            buyerFromWallet(event.billingDetails),
            (clientSecret) =>
                stripe.confirmPayment({
                    elements,
                    clientSecret,
                    confirmParams: { return_url: window.location.origin + window.location.pathname },
                    redirect: 'if_required'
                }),
            () => event.paymentFailed({ reason: 'fail' })
        );
    };

    // Dev only, see `mockWallet`.
    const [mock] = useState(mockWallet);
    useEffect(() => {
        if (mock) onAvailabilityChange(mock);
    }, [mock, onAvailabilityChange]);
    if (mock) {
        return (
            <div className={className}>
                <MockWalletButton
                    wallet={mock}
                    amount={amount}
                    onPay={(buyer, confirm) => pay(buyer, confirm)}
                />
                {message && <ErrorLabel error={message} />}
            </div>
        );
    }

    return (
        <div className={cn(!isAvailable && 'hidden', className)}>
            <ExpressCheckoutElement
                options={{
                    // The wallets that hand over email and name; Amazon Pay and PayPal do not, they are in the form.
                    paymentMethods: {
                        applePay: 'auto',
                        googlePay: 'auto',
                        amazonPay: 'never',
                        link: 'never',
                        paypal: 'never'
                    },
                    buttonType: { applePay: 'buy', googlePay: 'buy' },
                    buttonHeight: 48,
                    layout: { maxColumns: 1, overflow: 'never' }
                }}
                onReady={({ availablePaymentMethods }) => {
                    const wallet = availablePaymentMethods?.applePay
                        ? 'apple_pay'
                        : availablePaymentMethods?.googlePay
                          ? 'google_pay'
                          : null;
                    setIsAvailable(!!wallet);
                    onAvailabilityChange(wallet);
                }}
                // The tickets go to the wallet's email address. The name comes with the billing contact, which the
                // wallet fills in from the card; without it the name is not guaranteed.
                onClick={({ resolve }) => resolve({ emailRequired: true, billingAddressRequired: true })}
                onConfirm={onConfirm}
            />
            {message && <ErrorLabel error={message} />}
        </div>
    );
}

/**
 * The Apple Pay or Google Pay button (CIN-15367): the checkout footer's pay button while the wallet is the chosen
 * method. Deferred intent: the wallet sheet collects email and name, and only then is the PaymentIntent created.
 * Renders nothing where no wallet is available; mounted regardless, as it is what finds out.
 */
export default function ExpressCheckout({ amount, currency, ...props }: ExpressCheckoutProps): React.JSX.Element {
    return (
        <Elements
            stripe={stripePromise}
            // The server's PaymentIntent captures manually; the deferred Elements must say the same.
            options={{ mode: 'payment', amount, currency: currency.toLowerCase(), captureMethod: 'manual' }}>
            <ExpressCheckoutButtons
                {...props}
                amount={amount}
            />
        </Elements>
    );
}
